import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TutorialContent from './TutorialContent';
import ChatSidebar from '../chat/ChatSidebar';
import { apiRequest } from '../../utils/api';

/**
 * InteractiveTutorialV2 - Clean "Living Documentation" System
 * 
 * Simplified version that focuses on the core functionality:
 * - Display tutorial content as a living document
 * - Save as tutorial or convert to tasks
 * - Clean, focused interface without complex state management
 */
// Constants outside component to prevent recreation
const EMPTY_AI_ANALYSES = { promptBurst: null, aiCrawler: null };

const InteractiveTutorialV2 = ({
  isOpen = false,
  tutorialData = null,
  onClose = () => {},
  darkMode = false,
  className = ""
}) => {
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const tutorialRef = useRef(null);

  // Tutorial DOM capture hook
  const useTutorialDOMCapture = () => {
    const captureTutorialContent = useCallback(() => {
      // console.log('📖 [Tutorial DOM] Capturing tutorial content...');
      
      // Find the tutorial content container
      const tutorialContainer = document.querySelector('[data-tutorial-content]');
      if (!tutorialContainer) {
        // console.log('📖 [Tutorial DOM] ❌ Tutorial content container not found');
        return null;
      }
      
      // Extract visible text content (what user sees)
      const visibleContent = tutorialContainer.innerText || tutorialContainer.textContent || '';
      
      // Extract structured headings
      const headings = Array.from(tutorialContainer.querySelectorAll('h1, h2, h3, h4, h5, h6'))
        .map(h => ({
          level: parseInt(h.tagName.charAt(1)),
          text: h.textContent.trim()
        }));
      
      // Extract code blocks if any
      const codeBlocks = Array.from(tutorialContainer.querySelectorAll('pre, code'))
        .map(code => code.textContent.trim())
        .filter(text => text.length > 10); // Only meaningful code snippets
      
      return {
        fullContent: visibleContent,
        headings: headings,
        codeBlocks: codeBlocks,
        contentLength: visibleContent.length,
        wordCount: visibleContent.split(/\s+/).length,
        capturedAt: new Date().toISOString()
      };
    }, []);
    
    return { captureTutorialContent };
  };


  const { captureTutorialContent } = useTutorialDOMCapture();

  // Memoize captured tutorial content (only recapture when tutorial changes)
  const capturedContent = useMemo(() => {
    if (!currentTutorial) return null;
    return captureTutorialContent();
  }, [currentTutorial, captureTutorialContent]);

  // Helper function to extract the actual page URL being analyzed
  const extractPageUrlFromLocation = () => {
    const currentLocation = window.location.href;
    
    // Check if we're on a page analysis route
    const pageAnalysisMatch = currentLocation.match(/\/page-analysis\/([^?]+)/);
    if (pageAnalysisMatch) {
      try {
        // Decode the URL-encoded page URL
        return decodeURIComponent(pageAnalysisMatch[1]);
      } catch (e) {
        console.warn('Failed to decode page URL from location:', e);
      }
    }
    
    // Fallback to current location
    return currentLocation;
  };

  // Initialize tutorial when data is provided
  useEffect(() => {
    if (isOpen && tutorialData) {
      // console.log('🎓 [InteractiveTutorialV2] Initializing tutorial:', tutorialData);
      
      // Handle agent response structure (data.tutorial_content)
      if (tutorialData?.data?.tutorial_content) {
        const processedTutorial = {
          id: tutorialData.data?.tutorial_title || 'agent-tutorial',
          title: tutorialData.data?.tutorial_title || 'Agent Tutorial',
          tutorial_content: tutorialData.data.tutorial_content,
          audit_findings: tutorialData.data?.audit_findings,
          implementation_context: tutorialData.data?.implementation_context,
          difficulty: 'intermediate',
          estimatedTime: '15-20 minutes',
          originalData: tutorialData
        };
        setCurrentTutorial(processedTutorial);
      } else {
        // Handle legacy format or direct tutorial data
        setCurrentTutorial(tutorialData);
      }
    }
  }, [isOpen, tutorialData]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle tutorial save/export
  const handleSaveTutorial = () => {
    const tutorialData = {
      ...currentTutorial,
      savedAt: new Date()
    };
    
    // Create downloadable file
    const dataStr = JSON.stringify(tutorialData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentTutorial.id}-tutorial.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    // console.log('📖 Tutorial saved successfully');
  };

  // Task state management
  const [taskState, setTaskState] = useState({
    isSavedAsTask: false,
    isComplete: false,
    isLoading: false,
    error: null
  });

  // Initialize task state from tutorial data
  useEffect(() => {
    if (currentTutorial) {
      setTaskState({
        isSavedAsTask: !!currentTutorial?.task_id || currentTutorial?.is_saved_as_task || false,
        isComplete: currentTutorial?.status === 'implemented' || false,
        isLoading: false,
        error: null
      });
    }
  }, [currentTutorial]);

  // Debug: Log taskState changes
  useEffect(() => {
    // console.log('🔍 [InteractiveTutorialV2] TaskState changed:', taskState);
  }, [taskState]);

  // Handle save as task conversion
  const handleSaveAsTask = async () => {
    if (taskState.isSavedAsTask || taskState.isLoading) return;
    
    // console.log('🎯 [InteractiveTutorialV2] Save as Task clicked!');
    // console.log('🎯 [InteractiveTutorialV2] Tutorial data available:', currentTutorial);
    setTaskState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Extract agent data from the tutorial
      const agentData = {
        tutorial_title: currentTutorial.title,
        tutorial_content: currentTutorial.tutorial_content || currentTutorial.content,
        audit_findings: currentTutorial.audit_findings || 'No specific audit findings provided',
        implementation_context: currentTutorial.implementation_context || {
          page_url: extractPageUrlFromLocation(),
          task_type: 'general_optimization',
          related_audits: [],
          priority: 'medium'
        },
        rationale: currentTutorial.rationale || 'Tutorial created for SEO optimization'
      };
      
      // console.log('🎯 [InteractiveTutorialV2] Sending agent data to API:', agentData);
      
      // Call real API
      const { apiRequest } = await import('../../utils/api');
      const response = await apiRequest('/api/tutorials/save-as-task/', {
        method: 'POST',
        body: JSON.stringify(agentData)
      });
      
      // console.log('🎯 [InteractiveTutorialV2] Raw API Response object:', response);
      // console.log('🎯 [InteractiveTutorialV2] Response status:', response.status);
      // console.log('🎯 [InteractiveTutorialV2] Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API request failed:', response.status, errorText);
        throw new Error(`API request failed: ${response.status}`);
      }
      
      // Parse the JSON response
      const parsedResponse = await response.json();
      // console.log('🎯 [InteractiveTutorialV2] Parsed JSON response:', parsedResponse);
      
      if (parsedResponse && parsedResponse.success === true) {
        // Update state to show task created
        setTaskState(prev => ({
          ...prev,
          isSavedAsTask: true,
          isComplete: false,
          isLoading: false,
          error: null
        }));
        
        // Store the task ID for completion later
        setCurrentTutorial(prev => ({ ...prev, task_id: parsedResponse.task_id }));
        
        // console.log('✅ Tutorial saved as task:', parsedResponse.task_data);
        // console.log('🆔 Stored task_id for later use:', parsedResponse.task_id);
        // console.log('🔄 Updated taskState - should now show Mark Complete button');
      } else {
        throw new Error(parsedResponse?.message || 'Failed to save tutorial as task');
      }
    } catch (error) {
      console.error('Failed to save tutorial as task:', error);
      setTaskState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: `Failed to save tutorial as task: ${error.message}`
      }));
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setTaskState(prev => ({ ...prev, error: null }));
      }, 5000);
    }
  };

  // Handle mark task complete
  const handleMarkComplete = async () => {
    if (!taskState.isSavedAsTask || taskState.isComplete || taskState.isLoading || !currentTutorial.task_id) return;
    
    // console.log('🎯 [InteractiveTutorialV2] Mark Complete clicked!');
    // console.log('🎯 [InteractiveTutorialV2] Using task_id:', currentTutorial.task_id);
    setTaskState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Call real API
      const { apiRequest } = await import('../../utils/api');
      const response = await apiRequest(`/api/tutorials/${currentTutorial.task_id}/mark-complete/`, {
        method: 'PATCH'
      });
      
      // console.log('🎯 [InteractiveTutorialV2] Mark Complete API Response object:', response);
      // console.log('🎯 [InteractiveTutorialV2] Mark Complete Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Mark complete API request failed:', response.status, errorText);
        throw new Error(`API request failed: ${response.status}`);
      }
      
      // Parse the JSON response
      const parsedResponse = await response.json();
      // console.log('🎯 [InteractiveTutorialV2] Mark Complete Parsed JSON response:', parsedResponse);
      
      if (parsedResponse && parsedResponse.success === true) {
        // Update state to show completed
        setTaskState({
          isSavedAsTask: true,
          isComplete: true,
          isLoading: false
        });
        
        // console.log('✅ Task marked complete:', parsedResponse.task_data);
      } else {
        throw new Error(parsedResponse?.message || 'Failed to mark task complete');
      }
    } catch (error) {
      console.error('Failed to mark task complete:', error);
      setTaskState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: `Failed to mark task complete: ${error.message}`
      }));
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setTaskState(prev => ({ ...prev, error: null }));
      }, 5000);
    }
  };

  // Handle tutorial message callback (ChatSidebar already sends the message)
  const handleTutorialMessage = (message, context) => {
    // console.log('💬 [InteractiveTutorialV2] Tutorial message sent via ChatSidebar:', message);
    // ChatSidebar handles the API call with currentContext
    // This callback is just for any tutorial-specific side effects (currently none needed)
  };

  if (!isOpen || !currentTutorial) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-blue-900/70 to-indigo-900/80 backdrop-blur-lg"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      />

      {/* Main Tutorial Modal */}
      <motion.div
        ref={tutorialRef}
        data-tutorial-portal="true"
        data-tutorial-title={currentTutorial?.title || 'Interactive Tutorial'}
        data-tutorial-page-url={currentTutorial?.implementation_context?.page_url}
        data-tutorial-task-saved={false}
        data-tutorial-task-complete={false}
        initial={{ scale: 0.9, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 50 }}
        transition={{ 
          type: "spring", 
          damping: 25, 
          stiffness: 300,
          duration: 0.5 
        }}
        className={`
          relative w-full h-[90vh] max-w-7xl mx-auto
          ${darkMode ? 'bg-gray-900' : 'bg-white'}
          rounded-2xl shadow-2xl overflow-hidden
          border border-white/10
        `}
      >
        {/* Tutorial Content */}
        <div className="h-full flex">
          {/* Main Content Panel */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Tutorial Header */}
            <div className={`
              p-6 border-b relative overflow-hidden
              ${darkMode 
                ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-700' 
                : 'border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50'
              }
            `}>
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-4">
                  <motion.div 
                    className={`
                      w-12 h-12 rounded-2xl flex items-center justify-center
                      ${darkMode 
                        ? 'bg-gradient-to-br from-purple-600 to-blue-600' 
                        : 'bg-gradient-to-br from-purple-500 to-blue-500'
                      }
                      text-white shadow-lg
                    `}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </motion.div>
                  <div>
                    <h1 className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {currentTutorial.title}
                    </h1>
                    <div className="flex items-center gap-4 mt-1">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {currentTutorial.difficulty} • {currentTutorial.estimatedTime}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  
                  {/* Dynamic Task Button */}
                  {!taskState.isSavedAsTask ? (
                    <motion.button
                      onClick={handleSaveAsTask}
                      disabled={taskState.isLoading}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all
                        ${darkMode 
                          ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                          : 'bg-purple-500 hover:bg-purple-600 text-white'
                        }
                        ${taskState.isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      whileHover={!taskState.isLoading ? { scale: 1.05 } : {}}
                      whileTap={!taskState.isLoading ? { scale: 0.95 } : {}}
                      title="Save tutorial as a task for easy tracking"
                    >
                      {taskState.isLoading ? (
                        <>
                          <motion.div 
                            className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Save as Task
                        </>
                      )}
                    </motion.button>
                  ) : !taskState.isComplete ? (
                    <motion.button
                      onClick={handleMarkComplete}
                      disabled={taskState.isLoading}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all
                        ${darkMode 
                          ? 'bg-green-600 hover:bg-green-500 text-white' 
                          : 'bg-green-500 hover:bg-green-600 text-white'
                        }
                        ${taskState.isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      whileHover={!taskState.isLoading ? { scale: 1.05 } : {}}
                      whileTap={!taskState.isLoading ? { scale: 0.95 } : {}}
                      title="Mark this task as completed"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                      {taskState.isLoading ? (
                        <>
                          <motion.div 
                            className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          Completing...
                        </>
                      ) : (
                        <>
                          <motion.svg 
                            className="w-4 h-4" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.5 }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </motion.svg>
                          Mark Complete
                        </>
                      )}
                    </motion.button>
                  ) : (
                    <motion.div
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium
                        ${darkMode 
                          ? 'bg-green-900/30 text-green-300 border border-green-700' 
                          : 'bg-green-100 text-green-700 border border-green-300'
                        }
                      `}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                      title="Task completed!"
                    >
                      <motion.svg 
                        className="w-4 h-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 400, damping: 10 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </motion.svg>
                      Task Complete
                    </motion.div>
                  )}
                  
                  <motion.button
                    onClick={onClose}
                    className={`
                      p-2 rounded-xl transition-colors
                      ${darkMode 
                        ? 'hover:bg-gray-700 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                      }
                    `}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Exit Tutorial Mode"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                </div>
                
                {/* Error Message Display */}
                {taskState.error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-red-700">{taskState.error}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Tutorial Content */}
            <div className="flex-1 min-h-0">
              <TutorialContent
                tutorial={currentTutorial}
                darkMode={darkMode}
                className="h-full"
              />
            </div>
          </div>

          {/* Chat Panel - Full Agent Integration */}
          <div className="w-80">
          <ChatSidebar
            darkMode={darkMode}
            portalMode={true} // Prevent backend state reinit (same as LLMPortal)
            showNavigation={false}
            showTabs={false}
            isCollapsed={false}
            onToggleCollapse={() => {}} // Disabled in tutorial
            onSendMessage={handleTutorialMessage}
            onNavigateToAnalysis={() => {}} // No navigation from tutorial
            onLaunchLLMPortal={() => {}} // Disabled
            className="rounded-l-none border-l"
            recentAIAnalyses={EMPTY_AI_ANALYSES}
            // Tutorial context via proper dedicated path
            tutorialContentCapture={capturedContent}
            tutorialMetadata={{
              title: currentTutorial?.title,
              implementation_context: currentTutorial?.implementation_context,
              audit_findings: currentTutorial?.audit_findings,
              difficulty: currentTutorial?.difficulty,
              estimatedTime: currentTutorial?.estimatedTime,
              taskState: taskState
            }}
          />
          </div>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default InteractiveTutorialV2;
